import React, { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import type { ComboboxSize } from '@opendoor/atoms/combobox';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { globalObservability } from '@opendoor/observability/slim';
import { useObservability } from 'helpers/observability';
import useQueryParams, { getFirstQueryParam } from 'helpers/queryParams';
import { useRouter } from 'next/router';

import type { SellerLeadChannel } from '../../__generated__/zeus/types';
import { isOnboardingZeroAddress } from '../../components/api/onboardingZeroArm';
import { isMobileAppWebView } from '../../components/api/sellerLeadCakeRollout';
import type { TrackingTaxonomy } from '../../components/globals';
import { AddressConfirmationModal } from '../../components/landing-pages-v2/AddressConfirmationModal';
import type { AddressValidationResult, SellerAddressFlow } from '../../helpers/addressValidation';
import { useAddressValidationContext } from '../../helpers/AddressValidationContext';
import type { SelectedAddress } from '../../helpers/AddressValidationContext';
import { rememberManualAddressHandoff } from '../../helpers/manualAddressDraft';
import type { AutoCompletePrediction } from '../api/addressTypes';
import { redactGraphqlRequestErrorForTelemetry } from '../api/graphqlRequestError';
import { AddressInput } from './AddressInput';
import type { AddressInputProps } from './AddressInput';

interface NewSellerFlowAddressInputProps {
  trackingTaxonomy: TrackingTaxonomy;
  sellerFlow?: SellerAddressFlow;
  trackAddressEntry?: boolean;
  analyticsPrefix?: string;
  placeholder: string;
  hideLabel?: boolean;
  pageSetPath?: string;
  trackingKeywords?: string;
  channel?: SellerLeadChannel;
  actionButton?: AddressInputProps['actionButton'];
  actionButtonLayout?: AddressInputProps['actionButtonLayout'];
  inputClassName?: AddressInputProps['inputClassName'];
  popupClassName?: AddressInputProps['popupClassName'];
  icon?: ReactNode;
  leadingIcon?: ReactNode;
  confirmationMode?: 'relationship-select' | 'owner-first';
  listBoxAriaLabel?: AddressInputProps['listBoxAriaLabel'];
  /**
   * Renders a worded CTA beside the field, stacking below it on narrow screens,
   * by consuming AddressInput's single suffix action slot. The suffix slot itself
   * can also host a worded action with `actionButtonLayout="responsive"`. Takes
   * precedence over actionButton when both are provided.
   */
  sideActionButton?: (props: {
    onPress: () => void;
    isLoading: boolean;
    isReady: boolean;
  }) => ReactNode;
  size?: ComboboxSize;
  onAddressEntryInteraction?: () => void;
  /** Upstream surface (e.g. the customer dashboard) that started this flow, for attribution. */
  originSurface?: string;
}

// The CTA renders inside the combobox popup, below the suggestion list, so its
// horizontal padding tracks the atoms combobox item padding per size — otherwise
// its label sits a few pixels off the suggestions directly above it.
const MANUAL_ENTRY_PADDING: Record<ComboboxSize, string> = {
  xs: 'px-2.5 py-2',
  sm: 'px-4 py-3',
  md: 'px-5 py-4',
  lg: 'px-6 py-4',
};

const ManualEntryButton: React.FC<{
  onMouseDown: (e: React.MouseEvent) => void;
  onPress: () => void;
  size: ComboboxSize;
}> = ({ onMouseDown, onPress, size }) => {
  return (
    <Button
      analyticsName="cosmos-address-input-manual-entry"
      aria-label="Enter address manually instead"
      variant="primary"
      type="button"
      className={cn(
        'h-auto w-full justify-start border-0 bg-transparent text-body-md font-medium !text-od-content-accent-brand not-disabled:hover:bg-transparent not-disabled:active:bg-transparent not-disabled:hover:!text-od-content-hover-accent-brand',
        MANUAL_ENTRY_PADDING[size],
      )}
      onMouseDown={onMouseDown}
      onClick={onPress}
    >
      Enter address manually instead
    </Button>
  );
};

const NewSellerFlowAddressInput: React.FC<NewSellerFlowAddressInputProps> = ({
  actionButton,
  actionButtonLayout,
  inputClassName,
  popupClassName,
  channel,
  hideLabel,
  icon,
  leadingIcon,
  confirmationMode,
  listBoxAriaLabel = 'addresses',
  onAddressEntryInteraction,
  originSurface,
  pageSetPath,
  placeholder,
  sideActionButton,
  sellerFlow,
  trackAddressEntry = true,
  trackingKeywords,
  trackingTaxonomy,
  size = 'lg',
}) => {
  const router = useRouter();
  const { trackJourneyEvent } = useObservability();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const sideActionButtonPressRef = React.useRef<() => void>(() => undefined);
  const [sideActionButtonState, setSideActionButtonState] = useState({
    isLoading: false,
    isRegistered: false,
  });

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  // Locks the field while the modal reads the arm or hands off; a second pick would start a second handoff.
  const [handoffPending, setHandoffPending] = useState(false);
  const handoffPendingRef = React.useRef(false);
  const handleHandoffPendingChange = useCallback((pending: boolean) => {
    handoffPendingRef.current = pending;
    setHandoffPending(pending);
  }, []);
  const [predictions, setPredictions] = useState<AutoCompletePrediction[]>([]);
  const [addressRequestError, setAddressRequestError] = useState<string>();

  const {
    validatedAddress,
    setValidatedAddress,
    setAddressResults,
    setFirstPrediction,
    setPartnerReferralId,
    setPageSetPath,
    setTrackingKeywords,
    setChannel,
  } = useAddressValidationContext();

  const { partner_referral } = useQueryParams();
  const partnerReferral = getFirstQueryParam(partner_referral);

  const handleSubmitHandlerChange = useCallback(
    ({ isLoading, onPress }: { isLoading: boolean; onPress: () => void }) => {
      sideActionButtonPressRef.current = onPress;
      setSideActionButtonState((current) =>
        current.isRegistered && current.isLoading === isLoading
          ? current
          : { isLoading, isRegistered: true },
      );
    },
    [],
  );

  const handleSideActionButtonPress = useCallback(() => {
    if (handoffPendingRef.current) return;
    sideActionButtonPressRef.current();
  }, []);

  useEffect(() => {
    if (partnerReferral) {
      setPartnerReferralId(partnerReferral);
    }
    if (pageSetPath) {
      setPageSetPath(pageSetPath);
    }
    if (trackingKeywords) {
      setTrackingKeywords(trackingKeywords);
    }
    if (channel) {
      setChannel(channel);
    }
    // oxlint-disable-next-line react/exhaustive-effect-dependencies, react/exhaustive-deps -- react19-baseline: pre-existing, do not copy. Fix the dependency array rather than suppressing. Add the missing dependencies rather than suppressing
  }, [partnerReferral, pageSetPath, trackingKeywords, channel]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const captureError = useCallback((error: unknown) => {
    globalObservability
      .getSentryClient()
      .captureException?.(redactGraphqlRequestErrorForTelemetry(error));
  }, []);

  const handleAddressRequestError = useCallback(
    (error: unknown) => {
      captureError(error);
      setAddressRequestError('We can’t look up this address right now. Please try again.');
    },
    [captureError],
  );

  const handleManualEntryPress = useCallback(() => {
    if (handoffPendingRef.current) return;
    try {
      onAddressEntryInteraction?.();
    } catch (e) {
      captureError(e);
    }

    const searchSessionId =
      new URLSearchParams(window.location.search).get('searchSessionId') ?? undefined;
    rememberManualAddressHandoff({
      query: searchTerm,
      keepEdits: true,
      searchSessionId,
    });
    void router.push({
      pathname: '/manual-address-entry',
      query: {
        ...(trackingKeywords != null && { trackingKeywords }),
        ...(partnerReferral != null && { partnerReferralId: partnerReferral }),
        ...(pageSetPath != null && { pageSetPath }),
        ...(channel != null && { channel }),
        ...(originSurface != null && { originSurface }),
        ...(searchSessionId ? { searchSessionId } : {}),
      },
    });
  }, [
    router,
    trackingKeywords,
    partnerReferral,
    pageSetPath,
    channel,
    originSurface,
    searchTerm,
    onAddressEntryInteraction,
    captureError,
  ]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const handlePredictionsChange = (newPredictions: AutoCompletePrediction[]) => {
    setPredictions(newPredictions);
    if (newPredictions.length > 0) {
      setFirstPrediction(newPredictions[0]);
    }
  };

  const handleSearchTermChange = useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    setAddressRequestError(undefined);
  }, []);

  const customAction =
    !handoffPending && predictions.length < 5 && searchTerm !== '' && isFocused ? (
      <ManualEntryButton
        onMouseDown={handleMouseDown}
        onPress={handleManualEntryPress}
        size={size}
      />
    ) : undefined;
  const isSideActionButtonReady =
    !handoffPending &&
    sideActionButtonState.isRegistered &&
    searchTerm.trim() !== '' &&
    predictions.length > 0;

  const handleAddressValidated = (validationResult: AddressValidationResult) => {
    if (handoffPendingRef.current) return;
    const { address: validatedAddr, results, needsManualEntry } = validationResult;
    setAddressRequestError(undefined);

    const selectedAddress: SelectedAddress = {
      id: validatedAddr.id,
      street1: validatedAddr.street1,
      city: validatedAddr.city,
      state: validatedAddr.state,
      postal_code: validatedAddr.postal_code,
      unit: validatedAddr.unit,
      latitude: validatedAddr.latitude,
      longitude: validatedAddr.longitude,
      result: results,
      source: validatedAddr.source,
    };

    setAddressResults(results);
    setValidatedAddress(selectedAddress);

    if (needsManualEntry) {
      try {
        onAddressEntryInteraction?.();
      } catch (e) {
        captureError(e);
      }

      const searchSessionId =
        new URLSearchParams(window.location.search).get('searchSessionId') ?? undefined;
      rememberManualAddressHandoff({
        searchSessionId,
        fields: {
          street: selectedAddress.street1 ?? '',
          unit: selectedAddress.unit ?? '',
          city: selectedAddress.city ?? '',
          state: selectedAddress.state ?? '',
          postalCode: selectedAddress.postal_code ?? '',
        },
      });
      void router.push({
        pathname: '/manual-address-entry',
        query: {
          ...(partnerReferral != null && { partnerReferralId: partnerReferral }),
          ...(pageSetPath != null && { pageSetPath }),
          ...(trackingKeywords != null && { trackingKeywords }),
          ...(channel != null && { channel }),
          ...(originSurface != null && { originSurface }),
          ...(searchSessionId ? { searchSessionId } : {}),
        },
      });
      return;
    }

    if (
      selectedAddress.id &&
      !results.includes('ADDRESS_MISSING_SUB_PREMISE') &&
      !isMobileAppWebView()
    ) {
      handleHandoffPendingChange(true);
      void isOnboardingZeroAddress(selectedAddress.id);
    }
    setIsConfirmationModalOpen(true);
  };

  const handleAddressSelect = (address: SelectedAddress) => {
    try {
      if (trackAddressEntry)
        trackJourneyEvent(
          'sell',
          'addressEntryV2',
          {
            street1: address.street1,
            city: address.city,
            state: address.state,
            postalCode: address.postal_code,
            unit: address.unit,
            latitude: address.latitude,
            longitude: address.longitude,
            sourcePage: router.asPath,
            campaign: getFirstQueryParam(router.query.utm_campaign),
          },
          { flush: true },
        );
    } catch (e) {
      captureError(e);
    }

    try {
      onAddressEntryInteraction?.();
    } catch (e) {
      captureError(e);
    }
  };

  return (
    // Box is flex by default; the grid class wins for the side-button layout,
    // which keeps the CTA out of the field's trailing slot and drops it to full
    // width underneath once the row stops fitting.
    <Box
      className={cn(
        'flex-1',
        sideActionButton
          ? 'grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 max-[480px]:grid-cols-1'
          : 'flex-col',
      )}
    >
      <Col className="gap-2 od-address-input">
        <Col className="relative cta-input-form">
          <AddressInput
            id="home-address-input"
            label="Enter your home address"
            hideLabel={hideLabel}
            placeholder={placeholder}
            disabled={handoffPending}
            isResolvingSelection={handoffPending}
            onAddressSelect={handleAddressSelect}
            onAddressValidated={handleAddressValidated}
            validateAddress
            addressLookup={sellerFlow}
            onError={handleAddressRequestError}
            onPredictionsChange={handlePredictionsChange}
            onSearchTermChange={handleSearchTermChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            customAction={customAction}
            listBoxAriaLabel={listBoxAriaLabel}
            actionButton={sideActionButton ? undefined : actionButton}
            actionButtonLayout={actionButtonLayout}
            inputClassName={inputClassName}
            popupClassName={popupClassName}
            onSubmitHandlerChange={sideActionButton ? handleSubmitHandlerChange : undefined}
            icon={icon}
            leadingIcon={leadingIcon}
            size={size}
            error={Boolean(addressRequestError)}
          />
        </Col>
        {addressRequestError ? (
          <Text as="p" role="alert" className="text-body-sm text-od-content-error">
            {addressRequestError}
          </Text>
        ) : null}
      </Col>
      {/* oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render */}
      {sideActionButton?.({
        onPress: handleSideActionButtonPress,
        isLoading: sideActionButtonState.isLoading,
        isReady: isSideActionButtonReady,
      })}
      {isConfirmationModalOpen && validatedAddress && (
        <AddressConfirmationModal
          key={validatedAddress.id ?? validatedAddress.street1}
          sellerFlow={sellerFlow}
          confirmationMode={confirmationMode}
          onHandoffPendingChange={handleHandoffPendingChange}
          setIsOpen={setIsConfirmationModalOpen}
          isOpen={isConfirmationModalOpen}
          partnerReferralId={partnerReferral}
          initialAddress={validatedAddress}
          pageSetPath={pageSetPath}
          trackingKeywords={trackingKeywords}
          channel={channel}
          trackingTaxonomy={trackingTaxonomy}
          originSurface={originSurface}
        />
      )}
    </Box>
  );
};

export default NewSellerFlowAddressInput;
